class SensicReviewScroller extends HTMLElement {
  connectedCallback() {
    this.native = this.querySelector('.jdgm-carousel-wrapper');
    if (!this.native) return;
    this.locale = (this.dataset.locale || 'en').toLowerCase().split('-')[0];
    this.onTranslationClick = event => {
      const button = event.target.closest('[data-review-original]');
      if (!button || !this.track?.contains(button)) return;
      const card = button.closest('.jdgm-carousel-item');
      const texts = this.cardTexts.get(card);
      if (!texts) return;
      event.stopPropagation();
      const original = button.getAttribute('aria-pressed') !== 'true';
      this.setReviewText(card, original ? texts.original : texts.translated);
      button.setAttribute('aria-pressed', String(original));
      button.textContent = original ? this.labels.translation : this.labels.original;
    };
    this.addEventListener('click', this.onTranslationClick);
    this.observer = new MutationObserver(() => this.sync());
    this.observer.observe(this.native, {childList:true,subtree:true,characterData:true});
    this.sync();
    if (this.dataset.translationsUrl && !this.translations) {
      this.controller = new AbortController();
      fetch(this.dataset.translationsUrl, {signal: this.controller.signal})
        .then(response => { if (!response.ok) throw new Error('Review translations unavailable'); return response.json(); })
        .then(data => {
          if (!this.isConnected) return;
          this.translations = data.reviews;
          this.labels = data.labels[this.locale];
          this.signature = null;
          this.sync();
        })
        .catch(() => { /* Keep the original reviews if translations cannot load. */ });
    }
  }
  sourceHash(texts) {
    const source = [texts.title, texts.body].map(text => text.replace(/\s+/g, ' ').trim()).join('\n');
    let hash = 2166136261;
    for (let i = 0; i < source.length; i++) hash = Math.imul(hash ^ source.charCodeAt(i), 16777619);
    return (hash >>> 0).toString(16);
  }
  setReviewText(card, texts) {
    const title = card.querySelector('.jdgm-carousel-item__review-title');
    const body = card.querySelector('.jdgm-carousel-item__review-body');
    if (title) { title.textContent = texts.title; title.lang = texts.language; }
    if (body) { body.textContent = texts.body; body.lang = texts.language; }
  }
  translateCard(card) {
    const entry = this.translations?.[card.dataset.reviewId];
    const translated = entry?.[this.locale];
    if (!translated || !this.labels) return;
    const original = {
      title: card.querySelector('.jdgm-carousel-item__review-title')?.textContent || '',
      body: card.querySelector('.jdgm-carousel-item__review-body')?.textContent || '',
      language: entry.sourceLanguage
    };
    // A changed review must never display a translation of its previous content.
    if (this.sourceHash(original) !== entry.hash) return;
    const localized = {...original, ...translated, language: this.locale};
    this.setReviewText(card, localized);
    this.cardTexts.set(card, {original, translated: localized});
    card.dataset.translated = this.locale;
    const controls = document.createElement('div');
    controls.className = 'sensic-review-scroller__translation';
    const label = document.createElement('span');
    label.textContent = this.labels.translated + ' ·';
    const button = document.createElement('button');
    button.type = 'button';
    button.dataset.reviewOriginal = '';
    button.setAttribute('aria-pressed', 'false');
    button.textContent = this.labels.original;
    controls.append(label, button);
    card.querySelector('.jdgm-carousel-item__review')?.append(controls);
  }
  sync() {
    const heading = this.native.querySelector('.jdgm-carousel-title');
    if (heading && this.dataset.heading && heading.textContent !== this.dataset.heading) heading.textContent = this.dataset.heading;
    const items = [...this.native.querySelectorAll('.jdgm-carousel-item')].filter(item => item.textContent.trim());
    const signature = items.map(item => item.textContent.trim()).join('|');
    if (!items.length || signature === this.signature) return;
    this.signature = signature;
    this.cardTexts = new WeakMap();
    this.track?.remove();
    this.track = document.createElement('div');
    this.track.className = 'sensic-review-scroller__window';
    this.track.setAttribute('tabindex', '0');
    const belt = document.createElement('div');
    belt.className = 'sensic-review-scroller__belt';
    const group = document.createElement('div');
    group.className = 'sensic-review-scroller__group';
    const seen = new Set();
    items.forEach(item => {
      const text = item.textContent.trim();
      if (seen.has(text)) return;
      seen.add(text);
      const card = item.cloneNode(true);
      card.removeAttribute('style');
      card.removeAttribute('id');
      card.querySelectorAll('[id]').forEach(el => el.removeAttribute('id'));
      card.querySelectorAll('.jdgm-carousel-item__review-title, .jdgm-carousel-item__review-body').forEach(text => {
        text.removeAttribute('style');
        text.classList.remove('jdgm-ellipsis', 'jdgm-line-clamp');
      });
      card.querySelectorAll('img').forEach(image => {
        const standardSource = image.getAttribute('data-src');
        const retinaSource = image.getAttribute('data-src-retina');
        const source = retinaSource || standardSource;
        if (!source) return;
        image.setAttribute('src', source);
        if (standardSource && retinaSource) {
          image.setAttribute('srcset', `${standardSource} 1x, ${retinaSource} 2x`);
        }
        image.removeAttribute('data-src');
        image.removeAttribute('data-src-retina');
        image.classList.remove('jdgm-lazyload');
      });
      this.translateCard(card);
      group.append(card);
    });
    belt.append(group);
    if (group.children.length > 2) {
      const copy = group.cloneNode(true);
      copy.setAttribute('aria-hidden','true');
      copy.setAttribute('inert','');
      belt.append(copy);
      belt.classList.add('is-moving');
    }
    this.track.append(belt);
    this.native.after(this.track);
    this.native.classList.add('sensic-review-scroller__source');
  }
  disconnectedCallback() {
    this.observer?.disconnect();
    this.controller?.abort();
    this.removeEventListener('click', this.onTranslationClick);
  }
}
if (!customElements.get('sensic-review-scroller')) customElements.define('sensic-review-scroller', SensicReviewScroller);
